import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import { Loader2, CheckCircle, XCircle, RefreshCw, ExternalLink } from 'lucide-react';
import { useMandateContext } from '../../contexts/mandate-context';
import { useAuth } from '../../contexts/auth-context';
import { supabase } from '../../integrations/supabase/client';
import { logger } from '../../utils/logger';

interface PayPalConnection {
  id: string;
  paypal_account_email: string;
  connection_status: 'active' | 'disconnected' | 'error';
  last_synced_at: string | null;
  created_at: string;
}

const PayPalIntegrationSection: React.FC = () => {
  const { selectedMandateId } = useMandateContext();
  const { user, profile, organization } = useAuth();

  const [connection, setConnection] = useState<PayPalConnection | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [processingJobId, setProcessingJobId] = useState<string | null>(null);

  useEffect(() => {
    logger.info('PayPal useEffect: selectedMandateId changed', { selectedMandateId, userId: user?.id });
    if (selectedMandateId && user?.id) {
      fetchConnectionStatus();
    }
  }, [selectedMandateId, user]);

  const fetchConnectionStatus = async () => {
    if (!selectedMandateId || !user?.id) {
      logger.info('PayPal fetchConnectionStatus: Missing selectedMandateId or user.id', { selectedMandateId, userId: user?.id });
      return;
    }
    
    logger.info('PayPal fetchConnectionStatus: Starting fetch for mandate:', selectedMandateId);
    setLoading(true);
    setError(null);
    
    try {
      const { data, error } = await supabase
        .from('paypal_connections')
        .select('*')
        .eq('mandate_id', selectedMandateId)
        .maybeSingle();

      logger.info('PayPal fetchConnectionStatus: Query result', { data, error });

      if (error) throw error;

      if (data) {
        logger.info('PayPal fetchConnectionStatus: Found connection', data);
        setConnection({
          id: data.id,
          paypal_account_email: data.paypal_account_email,
          connection_status: (data.connection_status as 'active' | 'disconnected' | 'error'),
          last_synced_at: data.last_synced_at,
          created_at: data.created_at,
        });
      } else {
        logger.info('PayPal fetchConnectionStatus: No connection found for mandate:', selectedMandateId);
        setConnection(null);
      }
    } catch (err) {
      logger.error('Error fetching PayPal connection:', err);
      setError('Fehler beim Laden der PayPal-Verbindung.');
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async () => {
    if (!selectedMandateId || !user?.id || !organization?.id) {
      setError('Bitte wählen Sie ein Mandat aus und stellen Sie sicher, dass Sie angemeldet sind.');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Verwende Edge Function für PayPal OAuth
      logger.info('Calling PayPal OAuth Edge Function with:', {
        mandateId: selectedMandateId,
        organizationId: organization.id
      });

      const { data, error } = await supabase.functions.invoke('paypal-oauth', {
        body: {
          mandateId: selectedMandateId,
          organizationId: organization.id
        }
      });

      logger.info('PayPal OAuth Edge Function response:', { data, error });

      if (error) {
        logger.error('PayPal OAuth Edge Function error:', error);
        throw new Error(`PayPal OAuth setup failed: ${error.message}`);
      }

      if (!data?.success || !data?.oauthUrl) {
        logger.error('Invalid PayPal OAuth response:', data);
        throw new Error('Invalid response from PayPal OAuth service');
      }

      logger.info('PayPal OAuth URL generated successfully:', data.oauthUrl);
      
      // Redirect zu PayPal OAuth
      window.location.href = data.oauthUrl;
      
    } catch (err) {
      logger.error('Error connecting to PayPal:', err);
      
      // Fallback: Versuche direkten OAuth-Aufruf
      try {
        logger.info('Attempting fallback PayPal OAuth...');
        const clientId = 'AW7yCswnz2xoj4DU3QDubkRZX8hsdasPrIkmgxpz4PqWO7p8RL0zw_AxY1rQwIn4o1djl_QYRUz3S1i3';
        const redirectUri = encodeURIComponent('https://ampliofinance.ch/paypal/callback');
        const state = `${selectedMandateId}_${organization.id}`;
        const paypalOAuthUrl = `https://www.paypal.com/signin/authorize?client_id=${clientId}&response_type=code&scope=openid+profile+email&redirect_uri=${redirectUri}&state=${state}`;
        
        logger.info('Fallback PayPal OAuth URL:', paypalOAuthUrl);
        window.location.href = paypalOAuthUrl;
      } catch (fallbackErr) {
        logger.error('Fallback PayPal OAuth also failed:', fallbackErr);
        setError(`Fehler beim Verbinden mit PayPal: ${err.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setSelectedFile(file);
  };

  const handleManualImport = async () => {
    if (!selectedMandateId || !organization?.id) {
      setError('Bitte wählen Sie ein Mandat aus und Organisation muss gesetzt sein.');
      return;
    }
    if (!selectedFile) {
      setError('Bitte wählen Sie eine PayPal CSV-Datei aus.');
      return;
    }

    try {
      setUploading(true);
      setError(null);

      // 1) Upload file to storage
      const ext = selectedFile.name.split('.').pop()?.toLowerCase() || 'csv';
      const storagePath = `paypal/${selectedMandateId}/${Date.now()}_${selectedFile.name}`;

      const { error: uploadError } = await supabase.storage
        .from('file-uploads')
        .upload(storagePath, selectedFile, { contentType: 'text/csv', upsert: false });

      if (uploadError) throw uploadError;

      // 2) Create file_uploads record
      const { data: fileUpload, error: insertError } = await supabase
        .from('file_uploads')
        .insert({
          file_name: selectedFile.name,
          file_path: storagePath,
          file_type: ext,
          file_size: selectedFile.size,
          mandate_id: selectedMandateId,
          organization_id: organization.id,
          is_demo: false,
          processing_status: 'queued'
        })
        .select()
        .single();

      if (insertError) throw insertError;

      // 3) Invoke parser Edge Function
      const { data, error: fnError } = await supabase.functions.invoke('ai-process-transactions', {
        body: {
          fileUploadId: fileUpload.id,
          mandateId: selectedMandateId,
          organizationId: organization.id
        }
      });

      if (fnError) throw fnError;

      setProcessingJobId(data?.jobId || null);
      logger.info('Manual PayPal import started', { uploadId: fileUpload.id, jobId: data?.jobId });

      // Refresh any dependent views/state
      await fetchConnectionStatus();
    } catch (err: any) {
      logger.error('Manual PayPal import failed', err);
      setError(err?.message || 'Fehler beim Import der PayPal CSV-Datei.');
    } finally {
      setUploading(false);
    }
  };

  const handleDisconnect = async () => {
    if (!selectedMandateId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const { error } = await supabase
        .from('paypal_connections')
        .delete()
        .eq('mandate_id', selectedMandateId);

      if (error) throw error;

      setConnection(null);
    } catch (err) {
      logger.error('Error disconnecting PayPal:', err);
      setError('Fehler beim Trennen der PayPal-Verbindung. Bitte versuchen Sie es erneut.');
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    if (!connection?.id || !selectedMandateId || !user?.id || !organization?.id) return;
    
    // Guard: Only sync if connection is active
    if (connection.connection_status !== 'active') {
      setError('PayPal-Verbindung ist nicht aktiv. Bitte verbinden Sie Ihr PayPal-Konto erneut.');
      return;
    }
    
    setSyncing(true);
    setError(null);
    
    try {
      // Call the PayPal sync Edge Function
      const { data, error } = await supabase.functions.invoke('paypal-sync', {
        body: {
          connectionId: connection.id,
          mandateId: selectedMandateId,
          organizationId: organization.id
        }
      });

      if (error) throw error;

      logger.info('PayPal sync completed:', data);
      
      // Refresh connection status
      await fetchConnectionStatus();
    } catch (err) {
      logger.error('Error syncing PayPal:', err);
      setError('Fehler beim Synchronisieren der PayPal-Transaktionen. Bitte versuchen Sie es erneut.');
    } finally {
      setSyncing(false);
    }
  };

  const getStatusBadge = () => {
    if (!connection) return null;
    
    switch (connection.connection_status) {
      case 'active':
        return <Badge variant="default" className="bg-green-100 text-green-800"><CheckCircle className="w-3 h-3 mr-1" />Verbunden</Badge>;
      case 'disconnected':
        return <Badge variant="secondary"><XCircle className="w-3 h-3 mr-1" />Getrennt</Badge>;
      case 'error':
        return <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" />Fehler</Badge>;
      default:
        return <Badge variant="outline">Unbekannt</Badge>;
    }
  };

  const formatLastSync = (dateString: string | null) => {
    if (!dateString) return 'Nie';
    const date = new Date(dateString);
    return date.toLocaleString('de-DE');
  };

  return (
    <div className="space-y-4">
      {loading && (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="w-6 h-6 animate-spin" />
          <span className="ml-2">Lädt...</span>
        </div>
      )}
      
      {error && (
        <Alert variant="destructive">
          <XCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      
      {!loading && !connection && (
        <div className="space-y-4">
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <ExternalLink className="w-8 h-8 text-blue-600" />
            </div>
            <h3 className="text-lg font-medium mb-2">PayPal-Konto verbinden</h3>
            <p className="text-muted-foreground mb-6">
              Verbinden Sie Ihr PayPal-Konto, um Transaktionen automatisch zu importieren und zu klassifizieren.
            </p>
            <Button 
              onClick={handleConnect} 
              disabled={!selectedMandateId || loading}
              className="w-full max-w-xs"
            >
              PayPal-Konto verbinden
            </Button>
          </div>

          <div className="space-y-3 p-4 border rounded-lg">
            <h4 className="font-medium">Manueller Import (CSV)</h4>
            <p className="text-sm text-muted-foreground">
              Bis die PayPal-App freigeschaltet ist (ca. 7 Tage), können Sie Transaktionen per CSV importieren. Der Parser integriert die Daten vollständig in die Pipeline (KI-Klassifizierung, Mehrwährung, Abgleich).
            </p>
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={handleFileChange}
            />
            <div className="flex gap-2">
              <Button onClick={handleManualImport} disabled={uploading || !selectedFile}>
                {uploading ? 'Lade hoch…' : 'CSV importieren'}
              </Button>
              {processingJobId && (
                <span className="text-xs text-muted-foreground">
                  Job gestartet: {processingJobId}
                </span>
              )}
            </div>
          </div>
        </div>
      )}
      
      {!loading && connection && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <ExternalLink className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h4 className="font-medium">{connection.paypal_account_email}</h4>
                <p className="text-sm text-muted-foreground">
                  Letzte Synchronisation: {formatLastSync(connection.last_synced_at)}
                </p>
              </div>
            </div>
            {getStatusBadge()}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-muted/50 rounded-lg">
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">✓</div>
              <p className="text-sm text-muted-foreground">Automatische Klassifizierung</p>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">🔄</div>
              <p className="text-sm text-muted-foreground">Kreditkarten-Abgleich</p>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">📊</div>
              <p className="text-sm text-muted-foreground">Multi-Währung</p>
            </div>
          </div>
          
          <div className="flex space-x-3">
            <Button 
              onClick={handleSync} 
              disabled={syncing || connection.connection_status !== 'active'}
              variant="outline"
              className="flex-1"
            >
              {syncing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Synchronisiere...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Jetzt synchronisieren
                </>
              )}
            </Button>
            
            <Button 
              onClick={handleDisconnect} 
              disabled={loading}
              variant="destructive"
              className="flex-1"
            >
              Trennen
            </Button>
          </div>
          
          <div className="text-xs text-muted-foreground space-y-1">
            <p>• Transaktionen werden automatisch über die Haupt-KI-Pipeline klassifiziert</p>
            <p>• Alle Korrekturen werden für zukünftige Verbesserungen gelernt</p>
            <p>• Kreditkarten-Abhebungen werden automatisch abgeglichen</p>
            <p>• Multi-Währungsunterstützung mit SNB-Wechselkursen</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default PayPalIntegrationSection;
